import { useId } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FormDialog } from "../../ui/FormDialog";
import { SummaryValue } from "../../ui/SummaryValue";
import { copyButtonAriaLabel } from "./UpdateParamsDialog.logic";
import type { paths } from "../../api/generated/api-types";
import type { Theme } from "@mui/material/styles";
import type { JSX, ReactNode } from "react";

// Набор параметров и сводка принадлежат контракту SITE-24, поэтому выводятся из
// сгенерированных типов, а не переписываются руками: новое значение параметра
// или новое поле сводки приходит сюда ошибкой компиляции.
type UpdateSiteParamsRequestBody =
  paths["/sites/params/update"]["post"]["requestBody"]["content"]["application/json"];
type UpdateSiteParamsResponseBody =
  paths["/sites/params/update"]["post"]["responses"]["200"]["content"]["application/json"];

/** Обновляемый поисковый параметр; порядок ячеек строки — dr, organicKeywords, age. */
export type UpdateParameter = UpdateSiteParamsRequestBody["params"][number];

/** Состояние запроса SITE-24 — какой из документированных экранов диалога показан. */
export type UpdateRequestState = "input" | "sending" | "result" | "error" | "discard";

/** Сводка ответа SITE-24 — подтаблица «Итог обновления параметров» экрана SiteList. */
export type UpdateParamsSummary = UpdateSiteParamsResponseBody;

/** Статус действия «Скопировать» одного из списков итога. */
export type CopyStatus = "idle" | "copied" | "failed";

/** Какой из двух списков итога копирует действие «Скопировать». */
export type CopyTarget = "notFound" | "invalidLines";

interface UpdateParamsDialogProps {
  open: boolean;
  request: UpdateRequestState;
  /** Отмеченные параметры в порядке dr, organicKeywords, age. */
  selectedParameters: UpdateParameter[];
  rawData: string;
  /** Подсказка формата строки по набору параметров, например «site, dr, age». */
  lineFormat: string;
  nonEmptyLineCount: number;
  /** Предел непустых строк одного списка (SITE-24, расширение 2a). */
  maxLines: number;
  /** Сводка ответа; null во всех состояниях, кроме result. */
  summary: UpdateParamsSummary | null;
  /** Сообщение отказа над формой; null во всех состояниях, кроме error. */
  errorMessage: string | null;
  /** Статус «Скопировать» списка ненайденных адресов. */
  copyNotFoundStatus: CopyStatus;
  /** Статус «Скопировать» списка невалидных строк. */
  copyInvalidStatus: CopyStatus;
  onToggleParameter: (parameter: UpdateParameter) => void;
  onRawDataChange: (rawData: string) => void;
  onSubmit: () => void;
  /** «Отмена» из ввода или отказа: при непустом поле ведёт в подтверждение закрытия. */
  onCancel: () => void;
  /** «Закрыть» из итога: закрывает диалог и перезапрашивает список. */
  onCloseResult: () => void;
  onReturnToInput: () => void;
  /** «Закрыть без обновления» из подтверждения: закрывает диалог, введённый список теряется. */
  onDiscard: () => void;
  /** «Скопировать» у списка ненайденных адресов. */
  onCopyNotFound: () => void;
  /** «Скопировать» у списка невалидных строк. */
  onCopyInvalidLines: () => void;
}

const COPY_LABELS: Record<CopyStatus, string> = {
  idle: "Скопировать",
  copied: "Скопировано",
  failed: "Не удалось скопировать",
};

const PARAMETER_OPTIONS: { key: UpdateParameter; label: string }[] = [
  { key: "dr", label: "DR" },
  { key: "organicKeywords", label: "Organic Keywords" },
  { key: "age", label: "Age" },
];

const FORM_TITLE = "Обновление параметров сайтов";
const RESULT_TITLE = "Итог обновления параметров";
const DISCARD_TITLE = "Закрыть без обновления?";
const DISCARD_SUBTITLE =
  "Введённый список нигде не сохраняется и будет потерян. Чтобы применить его, вернитесь к вводу и нажмите «Обновить».";
const SENDING_NOTE = "Список применяется целиком — прервать обновление нельзя.";
const SUBMIT_BLOCKED_NOTE = "Отметьте хотя бы один параметр и вставьте список.";
const NOTHING_TO_UPDATE_SUBTITLE = "В списке не было ни одной непустой строки — обновлять было нечего.";
const NOTHING_CHANGED_SUBTITLE = "Ни одно значение не изменилось.";

// Высота области прокрутки списков итога — шагами шкалы отступов темы (theme.spacing),
// а не числом пикселей на месте.
const LIST_MAX_HEIGHT_STEPS = 25;

/**
 * Диалог обновления параметров сайтов вставленным списком: статическая разметка
 * состояний siteList/updateParams, -sending, -result, -error и -discard экрана
 * SiteList. Данные и обработчики приходят props'ами; поведение — в хуке экрана.
 */
export function UpdateParamsDialog(props: UpdateParamsDialogProps): JSX.Element {
  const {
    open,
    request,
    selectedParameters,
    rawData,
    lineFormat,
    nonEmptyLineCount,
    maxLines,
    summary,
    errorMessage,
    copyNotFoundStatus,
    copyInvalidStatus,
    onToggleParameter,
    onRawDataChange,
    onSubmit,
    onCancel,
    onCloseResult,
    onReturnToInput,
    onDiscard,
    onCopyNotFound,
    onCopyInvalidLines,
  } = props;
  const hintId = useId();
  const listFieldId = useId();
  const isSending = request === "sending";
  const hasParameters = selectedParameters.length > 0;
  const hasRows = rawData.trim() !== "";
  const canSubmit = hasParameters && hasRows;

  let formNote: string | null = null;
  if (isSending) {
    formNote = SENDING_NOTE;
  } else if (!canSubmit) {
    formNote = SUBMIT_BLOCKED_NOTE;
  }

  // TextField связывает поле с helperText через id `${id}-helper-text`; подсказка
  // формата стоит над полем и добавляется в то же описание, чтобы экранный чтец
  // назвал формат строки до ввода.
  const listDescribedBy = `${hintId} ${listFieldId}-helper-text`;

  const form = (
    <>
      {errorMessage !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <FormControl component="fieldset" disabled={isSending} sx={{ mb: 2 }}>
        <FormLabel component="legend">Какие параметры обновить</FormLabel>
        <FormGroup row>
          {PARAMETER_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.key}
              label={option.label}
              control={
                <Checkbox
                  checked={selectedParameters.includes(option.key)}
                  onChange={() => onToggleParameter(option.key)}
                />
              }
            />
          ))}
        </FormGroup>
      </FormControl>
      <Typography id={hintId} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Формат строки:{" "}
        <Typography component="span" variant="body2" color="text.primary" fontWeight={700}>
          {lineFormat}
        </Typography>{" "}
        — по одному сайту в строке. Разделитель ячеек — запятая, точка с запятой или табуляция.
        Пустая ячейка оставляет текущее значение. Обновляются только активные сайты пула.
      </Typography>
      <TextField
        id={listFieldId}
        label="Список сайтов"
        value={rawData}
        onChange={(event) => onRawDataChange(event.target.value)}
        multiline
        minRows={9}
        maxRows={14}
        fullWidth
        size="small"
        disabled={isSending}
        helperText={`Непустых строк: ${nonEmptyLineCount.toLocaleString("ru-RU")} из ${maxLines.toLocaleString("ru-RU")}`}
        slotProps={{ input: { "aria-describedby": listDescribedBy } }}
      />
    </>
  );

  const writtenParameterNames = PARAMETER_OPTIONS.filter((option) =>
    selectedParameters.includes(option.key),
  ).map((option) => option.label);
  // Перечисление читается человеком: «DR и Age», «DR, Organic Keywords и Age».
  const writtenParameterLabels =
    writtenParameterNames.length < 2
      ? writtenParameterNames.join("")
      : `${writtenParameterNames.slice(0, -1).join(", ")} и ${writtenParameterNames[writtenParameterNames.length - 1]}`;

  let resultSubtitle = `Значения ${writtenParameterLabels} записаны.`;
  let resultBody: ReactNode = null;
  if (summary !== null) {
    if (summary.totalLines === 0) {
      resultSubtitle = NOTHING_TO_UPDATE_SUBTITLE;
    } else if (summary.updatedCount === 0) {
      resultSubtitle = NOTHING_CHANGED_SUBTITLE;
    }
    resultBody = (
      <>
        <Box display="flex" gap={3} flexWrap="wrap" mb={2}>
          <SummaryValue label="Всего строк" value={summary.totalLines.toLocaleString("ru-RU")} />
          <SummaryValue label="Найдено строк" value={summary.foundCount.toLocaleString("ru-RU")} />
          <SummaryValue label="Обновлено строк" value={summary.updatedCount.toLocaleString("ru-RU")} />
          <SummaryValue label="Не найдено строк" value={summary.notFoundCount.toLocaleString("ru-RU")} />
          <SummaryValue label="Невалидных строк" value={summary.invalidCount.toLocaleString("ru-RU")} />
        </Box>
        {summary.notFound.length > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">
                Не найдены ({summary.notFoundCount.toLocaleString("ru-RU")})
              </Typography>
              <Button
                size="small"
                onClick={onCopyNotFound}
                aria-label={copyButtonAriaLabel(COPY_LABELS[copyNotFoundStatus], "notFound")}
              >
                {COPY_LABELS[copyNotFoundStatus]}
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: (theme: Theme) => theme.spacing(LIST_MAX_HEIGHT_STEPS) }}>
              <Table size="small" stickyHeader aria-label="Ненайденные сайты">
                <TableHead>
                  <TableRow>
                    <TableCell>Адрес из списка</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.notFound.map((address, index) => (
                    <TableRow key={`${index}-${address}`}>
                      <TableCell>{address}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        {summary.invalidLines.length > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">
                Невалидные строки ({summary.invalidCount.toLocaleString("ru-RU")})
              </Typography>
              <Button
                size="small"
                onClick={onCopyInvalidLines}
                aria-label={copyButtonAriaLabel(COPY_LABELS[copyInvalidStatus], "invalidLines")}
              >
                {COPY_LABELS[copyInvalidStatus]}
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: (theme: Theme) => theme.spacing(LIST_MAX_HEIGHT_STEPS) }}>
              <Table size="small" stickyHeader aria-label="Невалидные строки">
                <TableHead>
                  <TableRow>
                    <TableCell align="right">Строка</TableCell>
                    <TableCell>Содержимое</TableCell>
                    <TableCell>Причина</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.invalidLines.map((line) => (
                    <TableRow key={line.lineNumber}>
                      <TableCell align="right" className="tabular-nums">
                        {line.lineNumber}
                      </TableCell>
                      <TableCell>{line.raw}</TableCell>
                      <TableCell>{line.errorMessage}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </>
    );
  }

  let dialog: JSX.Element;
  if (request === "discard") {
    dialog = (
      <FormDialog
        open={open}
        title={DISCARD_TITLE}
        subtitle={DISCARD_SUBTITLE}
        startAction={
          <Button variant="contained" onClick={onReturnToInput}>
            Вернуться к вводу
          </Button>
        }
        endAction={
          <Button variant="outlined" color="error" onClick={onDiscard}>
            Закрыть без обновления
          </Button>
        }
        actionsNote={null}
        loading={false}
        size="sm"
        onClose={onReturnToInput}
      >
        {null}
      </FormDialog>
    );
  } else if (request === "result") {
    dialog = (
      <FormDialog
        open={open}
        title={RESULT_TITLE}
        subtitle={resultSubtitle}
        startAction={null}
        endAction={
          <Button variant="contained" onClick={onCloseResult}>
            Закрыть
          </Button>
        }
        actionsNote={null}
        loading={false}
        size="md"
        onClose={onCloseResult}
      >
        {resultBody}
      </FormDialog>
    );
  } else {
    dialog = (
      <FormDialog
        open={open}
        title={FORM_TITLE}
        subtitle={null}
        startAction={
          <Button onClick={onCancel} disabled={isSending}>
            Отмена
          </Button>
        }
        endAction={
          <Button variant="contained" onClick={onSubmit} disabled={!canSubmit} loading={isSending}>
            Обновить
          </Button>
        }
        actionsNote={formNote}
        loading={isSending}
        size="md"
        onClose={onCancel}
      >
        {form}
      </FormDialog>
    );
  }
  return dialog;
}
